import { CdkScrollable } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { MittanbudBannerComponent } from '@features/scheduler/unscheduled-projects/mittanbud-banner/mittanbud-banner.component';
import { MittanbudEnum } from '@features/scheduler/unscheduled-projects/mittanbud-banner/mittanbud-enum';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { ALL_FORM_FIELDS, nestedFormKeys } from '../create-client/create-client.constants';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ApiService } from '@core/api.service';
import { FiltersStoreService } from '@core/filters-store.service';
import { TranslateUiModule } from '@core/translation/translate-ui.module';
import { ClientModel } from '@features/clients/clients.models';
import { ClientStatusesEnum } from '@features/clients/clients-list/statuses.enum';
import { ProjectsService } from '@features/projects/services/projects.service';
import { SchedulerService } from '@features/scheduler/scheduler.service';
import { FilterTypeFields } from '@features/scheduler/scheduler-container/filters/filterTypes';
import { SegmentService } from '@services/common/segment.service';
import { StorageService } from '@services/common/storage.service';
import { CreateClientModalService } from '@shared/components/create-client/create-client-modal.service';
import { GooglePlacesAutocompleteComponent } from '@shared/components/projects-create/google-places-autocomplete/google-places-autocomplete.component';
import { ActionType } from '@shared/components/projects-create/project-modal.enum';
import { UserAccessTabComponent } from '@shared/components/projects-create/user-access-tab/user-access-tab.component';
import { SchedulerCalendarModule } from '@shared/components/scheduler-calendar/scheduler-calendar.module';
import { PeriodTypes, PeriodTypesListOptions } from '@shared/components/scheduler-period-picker/filterTypes';
import { SchedulerPeriodPickerModule } from '@shared/components/scheduler-period-picker/scheduler-period-picker.module';
import { LangEnum } from '@shared/enums/langs.enum';
import { LocationInfo } from '@shared/enums/project-gnr-bnr.enum';
import { FilterStorageModel } from '@shared/interfaces/filtersStore.model';
import { getEndOfMonth, getStartOfMonth } from '@shared/timetracking-filter/date.utility';
import { AppDatepickerModule } from '@ui-kit/app-datepicker/app-datepicker.module';
import { AppSelectModule } from '@ui-kit/app-select/app-select.module';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { BtnLoaderModule } from '@ui-kit/btn-loader/btn-loader.module';
import { FormControlErrorModule } from '@ui-kit/directives/form-control-error/form-control-error.module';
import { OnlyNumbersDirectiveModule } from '@ui-kit/directives/only-numbers/only-numbers.module';
import { LoaderModule } from '@ui-kit/loader/loader.module';
import { handleError } from '@ui-kit/utility/handleHttpErrorResponse';
import { CrudService } from 'collection';
import { GetEntityModel } from 'collection';
import { Collection, CollectionModel, Item } from 'collection';

@Component({
  selector: 'projects-create-new',
  templateUrl: './projects-create.component.html',
  styleUrls: ['./projects-create.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateUiModule,
    CdkScrollable,
    LoaderModule,
    ReactiveFormsModule,
    FormsModule,
    AppSelectModule,
    FormControlErrorModule,
    AppDatepickerModule,
    MatTooltipModule,
    OnlyNumbersDirectiveModule,
    SchedulerPeriodPickerModule,
    SchedulerCalendarModule,
    BtnLoaderModule,
    UserAccessTabComponent,
    GooglePlacesAutocompleteComponent,
    MittanbudBannerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectsCreateComponent implements OnInit, OnDestroy {
  collection: CollectionModel;
  clientsCollection: CollectionModel;
  projectsCollection: CollectionModel;
  projectItem: Item;
  clientItem: Item;
  clientModel: ClientModel;
  mainProjectModel: ClientModel;
  isSubproject = false;
  actionLabel: string;
  isEmployee: boolean;
  isUserAccess = false;
  mainTabTitle: string;
  skipValueChanges = false;

  filtersScheduler: FilterStorageModel;
  filterTypesEnum = FilterTypeFields;

  getProjectIdRequest: GetEntityModel;
  getSubProjectIdRequest: GetEntityModel;
  editMode = false;
  showScheduler: boolean;

  formGroup: UntypedFormGroup;
  private _destroyed$ = new Subject();

  accountNorway = null;
  addressValue = null;
  gnr = null;
  bnr = null;
  externalGnrBnrLink = null;
  initGnrBnr = false;
  LocationInfo = LocationInfo;
  mittanbudEnum = MittanbudEnum;

  @ViewChild('textArea') textArea: ElementRef;

  @ViewChild('projectNameInput', { static: false }) projectNameInput: ElementRef;

  constructor(
    private _projectsService: ProjectsService,
    private _api: ApiService,
    private _router: Router,
    private _crud: CrudService,
    private _toastService: ToasterService,
    private _cdr: ChangeDetectorRef,
    private _createClientModalService: CreateClientModalService,
    private _segmentService: SegmentService,
    private _schedulerService: SchedulerService,
    private _storage: StorageService,
    public _dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.showScheduler = this._schedulerService.$showScheduler.value;

    this._schedulerService.$showScheduler.pipe(takeUntil(this._destroyed$)).subscribe((show) => {
      this.showScheduler = show;
    });

    this.filtersScheduler = new FiltersStoreService(this._router).filters.projectSchedulerModal;

    this.isSubproject = this.data.item.isSubproject;

    this.mainTabTitle = this.isSubproject ? marker('Subproject info') : marker('Project info');

    this.initCreateEditForm();

    this.clientsCollection = new Collection({
      api: this._api.clients,
      params: { status: 'active' }
    });

    this.clientsCollection.loadByScrolling = true;

    this.projectsCollection = new Collection({
      api: this._crud.createEntity({ name: 'project/list-trigger' }),
      params: {
        'exclude_display_status[]': 'finished',
        expand: 'client',
        sort: 'name'
      }
    });

    this.projectsCollection.loadByScrolling = true;

    this.projectItem = new Item({
      api: this._api.projects,
      params: {
        expand: 'client,user,total_time_minutes,parent_project,display_status,project_location'
      }
    });

    this.getProjectIdRequest = this._projectsService.getProjectIdRequest;
    this.getSubProjectIdRequest = this._projectsService.getSubProjectIdRequest;

    this.isEmployee = this.data.isWorker;
  }

  changeTab(state: boolean): void {
    this.isUserAccess = state;
    this._cdr.markForCheck();
  }

  get nameControlError() {
    const nameControl = this.formGroup.get(ALL_FORM_FIELDS.name);
    return nameControl.touched && nameControl.errors;
  }

  get emailControlError() {
    const emailControl = this.formGroup.get(ALL_FORM_FIELDS.email);
    return emailControl.touched && emailControl.errors && emailControl.errors.email;
  }

  getTitle(): void {
    const createSubproject = marker('Create Subproject');
    const editSubproject = marker('Edit Subproject');
    const createProject = marker('Create Project');
    const editProject = marker('Edit Project');
    const estimateConverting = marker('Convert to Project');

    let actionType: ActionType;

    if (this.data.estimateConvertingData) {
      actionType = ActionType.EstimateConverting;
    } else if (this.isSubproject) {
      actionType = this.editMode ? ActionType.EditSubproject : ActionType.CreateSubproject;
    } else {
      actionType = this.editMode ? ActionType.EditProject : ActionType.CreateProject;
    }

    switch (actionType) {
      case ActionType.EstimateConverting:
        this.actionLabel = estimateConverting;
        break;
      case ActionType.EditSubproject:
        this.actionLabel = editSubproject;
        break;
      case ActionType.CreateSubproject:
        this.actionLabel = createSubproject;
        break;
      case ActionType.EditProject:
        this.actionLabel = editProject;
        break;
      case ActionType.CreateProject:
        this.actionLabel = createProject;
        break;
    }
  }

  setGeoControlValue(e) {
    if (e === '') {
      this.formGroup.patchValue({ project_location: null });
      this.initGnrBnr = false;
      return;
    }

    const lng = e.place.geometry.location.lng();
    const lat = e.place.geometry.location.lat();

    this.externalGnrBnrLink = `https://seeiendom.kartverket.no/?query=${e.address}`;
    this.formGroup.patchValue({ project_location: { lng, lat, address: e.address } });
  }

  onAddressInputChange(value): void {
    const projectObj = this.formGroup.get('project_location').value;

    if (!value['data']) {
      this.initGnrBnr = false;
    }

    if (!projectObj) {
      this.gnr = null;
      this.bnr = null;
      this.addressValue = '';
    }

    this.addressValue = value['data'];
  }

  initGnrBrnFields(): void {
    this.initGnrBnr = !this.initGnrBnr;
  }

  handleGnrBnr(event, type: string): void {
    const value: number = parseInt(event['target'].value);

    switch (type) {
      case LocationInfo.Gnr:
        this.formGroup.patchValue({
          project_location: {
            ...this.formGroup.get('project_location').value,
            gnr: value ? value : null
          }
        });
        break;
      case LocationInfo.Bnr:
        this.formGroup.patchValue({
          project_location: {
            ...this.formGroup.get('project_location').value,
            bnr: value ? value : null
          }
        });
        break;
    }
  }

  get displayGnrBnrButton() {
    return (
      (!this.editMode && this.accountNorway && !this.initGnrBnr && this.addressValue) ||
      (this.editMode && this.accountNorway && this.addressValue && !this.initGnrBnr && !(this.gnr || this.bnr))
    );
  }

  get displayGnrBnrFields() {
    return (this.initGnrBnr && this.addressValue) || this.gnr || this.bnr || (this.editMode && this.initGnrBnr);
  }

  close(): void {
    this._dialogRef.close();
  }

  closeModal(e: Event): void {
    // e.stopPropagation();
    // e.preventDefault();

    this.data.subject.next({
      width: 'md',
      confirmed: false,
      modal: this._dialogRef
    });

    this.close();
  }

  getClientsList({ search, page }): void {
    this.clientsCollection.setParams('page', page);
    search.length ? this.clientsCollection.setParams('name', search) : this.clientsCollection.clearParams('name');
    this.clientsCollection.search();
  }

  getProjectsList({ search, page }): void {
    this.projectsCollection.setParams('page', page);
    search.length
      ? this.projectsCollection.setParams('search_query', search)
      : this.projectsCollection.clearParams('search_query');
    this.projectsCollection.search();
  }

  getFormControl(controlName: string): AbstractControl {
    return this.formGroup.get(controlName);
  }

  selectedMainProject({ model }): void {
    if (model?.client_id && !model?.client?.delete_time && model.client.status === ClientStatusesEnum.Active) {
      this.setClientModel(model.client);
      this.formGroup.get('client_id').setValue(model?.client_id);
    } else {
      this.setClientModel({ name: 'None', id: null });
      this.formGroup.get('client_id').setValue(null);
    }

    this.getSubProjectIdRequest.get({ id: model.id }).subscribe((res) => {
      this.formGroup.get(ALL_FORM_FIELDS.display_id).setValue(res.data);
    });
  }

  private projectItemSubscription(subject: Observable<object>, saveAndSchedule?: boolean): Subscription {
    return subject.subscribe(
      (res: { data: any }) => {
        this.initSegmentEvent(res.data);
        this.data.subject.next({
          confirmed: true,
          modal: this._dialogRef,
          item: res.data,
          schedule: saveAndSchedule,
          estimateData: this.data.estimateConvertingData
        });

        this.close();
        this._toastService.show({
          type: 'success',
          message: `Project is successfully ${this.editMode ? 'updated' : 'created'}`
        });
      },
      (err) => {
        const errorResponse = err.error;
        handleError(errorResponse, this._toastService);
      }
    );
  }

  private createProject(saveAndSchedule?: boolean): void {
    const data = {
      ...this.formGroup.value,
      estimated_time_minutes: Math.round(this.formGroup.value.estimated_time_minutes * 60)
    };

    if (this.data.estimateConvertingData) {
      data['estimate_id'] = this.data.estimateConvertingData.id;
    }

    if (!data['parent_id']) {
      delete data['parent_id'];
    }

    this.checkProjectLocationData(data);
    this.projectItemSubscription(
      this.projectItem.createItem({
        data: data,
        path: 'project?expand=client'
      }),
      saveAndSchedule
    );
  }

  private updateProject(): void {
    const data = {
      ...this.formGroup.value,
      estimated_time_minutes: Math.round(this.formGroup.value.estimated_time_minutes * 60)
    };

    this.checkProjectLocationData(data);

    this.projectItemSubscription(
      this.projectItem.updateItem({
        data: data
      })
    );
  }

  checkProjectLocationData(data): void {
    if (!data['project_location']) {
      data['project_location'] = null;
    }
  }

  private initSegmentEvent(project): void {
    if (this.isSubproject) {
      this.initSegmentEventSubProject(project);
    } else {
      this.initSegmentEventProject(project);
    }
  }

  private initSegmentEventProject(project): void {
    const eventName = this.editMode ? 'Project Update' : 'Project Create';

    this._segmentService.track(eventName, {
      project_name: project.name,
      project_status: project.status
    });
  }

  private initSegmentEventSubProject(project): void {
    const eventName = this.editMode ? 'Sub-Project Update' : 'Sub-Project Create';

    this._segmentService.track(eventName, {
      project_name: project.name
    });
  }

  private initDateCheck(): void {
    this.getFormControl('end_date')
      .valueChanges.pipe(filter(() => !this.skipValueChanges))
      .subscribe((endDateValue) => {
        if (endDateValue) {
          const startDateControl = this.getFormControl('start_date');
          const endDate = new Date(endDateValue).getTime();
          const startDate = new Date(startDateControl.value).getTime();

          if (endDate <= startDate) {
            this.skipValueChanges = true;
            startDateControl.patchValue(endDateValue, { emitEvent: false });
            this.skipValueChanges = false;
          }
        }
      });

    this.getFormControl('start_date')
      .valueChanges.pipe(filter(() => !this.skipValueChanges))
      .subscribe((startDateValue) => {
        if (startDateValue) {
          const endDateControl = this.getFormControl('end_date');
          const startDate = new Date(startDateValue).getTime();
          const endDate = new Date(endDateControl.value).getTime();

          if (startDate >= endDate) {
            this.skipValueChanges = true;
            endDateControl.patchValue(startDateValue, { emitEvent: false });
            this.skipValueChanges = false;
          }
        }
      });
  }

  handleTabulation(e) {
    if (e.keyCode === 9) {
      const textArea = this.textArea.nativeElement;

      textArea.setRangeText('  ', textArea.selectionStart, textArea.selectionStart, 'end');
    }
  }

  setEditForm(data) {
    this.formGroup.patchValue({
      project_location: data.project_location || '',
      parent_id: data.parent_id,
      client_id: data.client_id,
      display_id: data.display_id,
      name: data.name,
      estimated_time_minutes: data.estimated_time_minutes / 60,
      price: data.price,
      description: data.description,
      start_date: data.start_date || '',
      end_date: data.end_date || '',
      assigned_users: this.data?.preSelectedData?.assigned_users || []
    });

    this.initClientModel(data.client);

    if (this.isSubproject) {
      this.initMainProjectModel({
        id: data.parent_id,
        name: data.parent_project
      });
    }

    const projectLocationData = data.project_location;

    if (this.editMode) {
      this.addressValue = data.project_location?.address;
      this.externalGnrBnrLink = `https://seeiendom.kartverket.no/?query=${projectLocationData.address}`;

      projectLocationData.gnr ? (this.gnr = projectLocationData.gnr) : null;
      projectLocationData.bnr ? (this.bnr = projectLocationData.bnr) : null;
    }
  }

  initCreateEditForm() {
    const maxEstimatedHours = 59999940 / 60;

    this.formGroup = new UntypedFormGroup({
      project_location: new UntypedFormControl(''),
      parent_id: new UntypedFormControl(''),
      client_id: new UntypedFormControl(''),
      display_id: new UntypedFormControl('', [Validators.maxLength(50)]),
      name: new UntypedFormControl(null, [Validators.required, Validators.maxLength(100)]),
      estimated_time_minutes: new UntypedFormControl('', Validators.max(maxEstimatedHours)),
      price: new UntypedFormControl(null),
      description: new UntypedFormControl('', Validators.maxLength(1000)),
      start_date: new UntypedFormControl(new Date().toJSON().substr(0, 10)),
      end_date: new UntypedFormControl(''),
      assigned_users: new UntypedFormControl([])
    });

    if (this.isSubproject) {
      this.formGroup.controls['parent_id'].setValidators([Validators.required]);
      this.formGroup.get('parent_id').updateValueAndValidity();
    }
  }

  initForm() {
    if (this.data.item.hasOwnProperty('id') && this.data.item.id) {
      this.editMode = true;
      this.projectItem
        .setParams('id', this.data.item.id)
        .load()
        .subscribe(() => {
          this.setEditForm(this.projectItem.data);
          this.focusNameInput();
        });
    } else {
      this.getNextProjectId();
      this.editMode = false;
    }
  }

  createClient(): void {
    this._createClientModalService
      .createClient()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(({ confirmed, item }) => {
        if (confirmed) {
          const control = this.formGroup.get('client_id');
          control.setValue(item.id);
          this.clientModel = item;
          this._cdr.markForCheck();
        }
      });
  }

  submit(saveAndSchedule = false): void {
    if (this.formGroup.valid && !this.editMode) {
      this.createProject(saveAndSchedule);
    } else if (this.formGroup.valid && this.editMode) {
      this.updateProject();
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  private setClientModel(client): void {
    this.clientModel = client;
  }

  selectClient({ model }): void {
    this.setClientModel(model);
  }

  private initClientModel(client) {
    this.clientModel = client;
  }

  private initMainProjectModel(project) {
    this.mainProjectModel = project;
  }

  private getNextProjectId(): void {
    if (!this.isSubproject) {
      this.getProjectIdRequest.get().subscribe((res) => {
        this.formGroup.get(ALL_FORM_FIELDS.display_id).setValue(res.data);
      });
    } else {
      // resetting date to initialize calendar filters
      this.formGroup.get('start_date').patchValue(new Date().toJSON().substr(0, 10));
    }
  }

  private focusNameInput(): void {
    setTimeout(() => {
      this.projectNameInput?.nativeElement?.focus();
    }, 400);
  }

  setFromCalendarDate(e): void {
    if (e) {
      this.filtersScheduler
        .set(this.filterTypesEnum.FromDate, getStartOfMonth(e))
        .set(this.filterTypesEnum.ToDate, getEndOfMonth(e));
      this.filtersScheduler.changed.next({ field: this.filterTypesEnum.FromDate });
    }
  }

  private initFilters() {
    this.formGroup.valueChanges.pipe(take(1)).subscribe((val) => {
      const periodObject = PeriodTypesListOptions.find((option) => option.value === PeriodTypes.Month);
      const fromDate = getStartOfMonth(val.fromDate) || periodObject.fromDate;
      const toDate = val.fromDate ? getEndOfMonth(fromDate) : periodObject.toDate;

      this.filtersScheduler.set(this.filterTypesEnum.Period, periodObject.value);
      this.filtersScheduler.set(this.filterTypesEnum.FromDate, fromDate);
      this.filtersScheduler.set(this.filterTypesEnum.ToDate, toDate);
      this.filtersScheduler.changed.next({ field: this.filterTypesEnum.FromDate });
    });
  }

  updateSelectedItems(data): void {
    const items = data.items;
    const type = data.type || items.type;

    switch (type) {
      case this.filterTypesEnum.Period:
        this.filtersScheduler.set(this.filterTypesEnum.FromDate, items.fromDate);
        this.filtersScheduler.set(this.filterTypesEnum.ToDate, items.toDate);
        this.filtersScheduler.changed.next({ field: this.filterTypesEnum.Period });
        break;
      case this.filterTypesEnum.ToDate:
        this.filtersScheduler.set(this.filterTypesEnum.ToDate, items.toDate);
        this.filtersScheduler.changed.next({ field: this.filterTypesEnum.ToDate });
        break;
      case this.filterTypesEnum.FromDate:
        this.filtersScheduler.set(this.filterTypesEnum.FromDate, items.fromDate);
        this.filtersScheduler.changed.next({ field: this.filterTypesEnum.FromDate });
        break;
    }
  }

  private setPreselectedData(): void {
    if (this.data.preSelectedData) {
      for (const key in this.data.preSelectedData) {
        if (this.data.preSelectedData.hasOwnProperty(key) && this.formGroup.get(key)) {
          this.formGroup.patchValue({ [key]: this.data.preSelectedData[key] });
        }
      }
    }
  }

  private setEstimateConvertingData(): void {
    if (this.data.estimateConvertingData) {
      const { client, hourQuantity, ...rest } = this.data.estimateConvertingData;
      const total_cost =
        this.data.estimateConvertingData?.total_cost !== undefined
          ? this.data.estimateConvertingData.total_cost
          : this.data.estimateConvertingData.summary?.total_cost || this.data.estimateConvertingData.summary?.totalCost || 0;
      const estimateData = { ...rest, price: total_cost, estimated_time_minutes: hourQuantity };

      for (const key in estimateData) {
        if (estimateData.hasOwnProperty(key) && this.formGroup.get(key)) {
          if (key !== 'client_id') {
            this.formGroup.patchValue({ [key]: estimateData[key] });
          } else if (key === 'client_id' && !client?.delete_time && client?.status !== ClientStatusesEnum.Inactive) {
            this.formGroup.patchValue({ [key]: estimateData[key] });
          }
        }
      }

      if (client?.status !== ClientStatusesEnum.Inactive && !client?.delete_time) {
        this.setClientModel(client);
      }
    }
  }

  getAccountCountry(): void {
    const user = this._storage.getItem('user');

    if (user.account.country.two_letter_code === LangEnum.Norway) {
      this.accountNorway = LangEnum.Norway;
    }
  }

  ngOnInit(): void {
    if (!this.isEmployee) {
      this.initFilters();
    }
    this.getAccountCountry();

    this.initForm();
    this.getTitle();
    this.initDateCheck();

    this.focusNameInput();

    this.setPreselectedData();
    this.setEstimateConvertingData();
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
  }
}
